import React, { useState, useEffect } from "react";
import firebase from 'firebase/compat/app';
import DisputeAIAnalysis from "./DisputeAIAnalysis";

const DisputeAnalysisContainer = ({ citation, dispute, locationRules, onQuickView }) => {
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  // Get a reference to the Cloud Function
  const analyzeDisputeFunction = firebase.functions().httpsCallable('analyzeDispute');

  // Fetch existing analysis when component loads
  useEffect(() => {
    if (dispute && dispute.id) {
      const fetchAnalysis = async () => {
        try {
          const db = firebase.firestore();
          const analysisDoc = await db.collection('aiAnalysis').doc(dispute.id).get();
          
          if (analysisDoc.exists) {
            setAnalysis(analysisDoc.data().analysis);
          }
        } catch (err) {
          console.error("Error fetching analysis:", err);
          // Don't show error to user, just log it
        }
      };
      
      fetchAnalysis();
    }
  }, [dispute]);

  // Function to analyze a dispute
  const handleAnalyzeDispute = async (citation, dispute, rules) => {
    setIsAnalyzing(true);
    setError(null);
    
    try {
      // Get similar past disputes for context
      let similarDisputes = [];
      try {
        const db = firebase.firestore();
        // Look for similar disputes based on location
        const similarDisputesQuery = await db
          .collection('aiTrainingData')
          .where('locationId', '==', citation.location || '')
          .orderBy('createdAt', 'desc')
          .limit(5)
          .get();
        
        similarDisputes = similarDisputesQuery.docs.map(doc => doc.data());
        
        // If not enough location-specific ones, get more general ones
        if (similarDisputes.length < 3) {
          const generalDisputesQuery = await db
            .collection('aiTrainingData')
            .orderBy('createdAt', 'desc')
            .limit(5 - similarDisputes.length)
            .get();
          
          similarDisputes = [
            ...similarDisputes,
            ...generalDisputesQuery.docs.map(doc => doc.data())
          ];
        }
      } catch (err) {
        console.log("Could not fetch similar disputes:", err);
        // Continue without similar disputes
      }
      
      // Call the Cloud Function with similar disputes
      const result = await analyzeDisputeFunction({
        citation,
        dispute,
        rules: rules || "",
        similarDisputes
      });
      
      if (result.data && result.data.success) {
        const analysisResult = result.data.analysis;
        
        // Store the analysis with a unique ID
        analysisResult.id = Date.now().toString();
        
        setAnalysis(analysisResult);
        
        // Store the analysis in Firestore (this is already done in the Cloud Function)
      } else {
        throw new Error("Analysis failed");
      }
    } catch (err) {
      console.error("Error analyzing dispute:", err);
      setError("Failed to analyze dispute. Please try again later.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Function to handle feedback
  const handleFeedback = async (feedbackData) => {
    try {
      const db = firebase.firestore();
      
      // Store feedback in Firestore
      await db.collection('aiTrainingData').add({
        ...feedbackData,
        locationId: citation.location || '',
        locationType: citation.location_type || '',
        violationType: citation.violation_type || '',
        disputeText: dispute.text || '',
        disputeReason: dispute.reason || '',
        hasEvidence: Array.isArray(dispute.evidence) && dispute.evidence.length > 0,
        evidenceCount: dispute.evidence ? dispute.evidence.length : 0,
        evidenceTypes: dispute.evidence ? dispute.evidence.map(e => e.name).join(', ') : '',
        validPaymentFound: analysis?.validPaymentFound || false,
        hadPaymentVerification: analysis?.validPaymentFound || false,
        paymentDetails: analysis?.paymentDetails || null,
        hasPhotoEvidence: citation.images && citation.images.length > 0,
        photoEvidenceCount: citation.images ? citation.images.length : 0,
        consideredPhotoEvidence: analysis?.consideredPhotoEvidence || false,
        consideredDisputeEvidence: analysis?.consideredDisputeEvidence || false,
        disputeEvidenceAnalysis: analysis?.disputeEvidenceAnalysis || '',
        adminAgreedWithVerification: analysis?.validPaymentFound ? feedbackData.wasHelpful : null,
        adminAgreedWithEvidenceAnalysis: analysis?.consideredDisputeEvidence ? feedbackData.wasHelpful : null,
        createdAt: firebase.firestore.FieldValue.serverTimestamp()
      });
      
      console.log("Feedback saved successfully");
    } catch (err) {
      console.error("Error saving feedback:", err);
      // Don't show error to user, just log it
    }
  };

  return (
    <>
      <DisputeAIAnalysis
        citation={citation}
        dispute={dispute}
        rules={locationRules}
        analysis={analysis}
        isAnalyzing={isAnalyzing}
        onAnalyze={handleAnalyzeDispute}
        onFeedback={handleFeedback}
        onQuickView={onQuickView}
      />
      
      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}
    </>
  );
};

export default DisputeAnalysisContainer; 